// =============================================
// 7. TIMELINE REPORT WITH PROPER IMPORTS  
// components/reports/TimelineReport.js
// =============================================

import React, { useMemo, useState } from 'react'
import { Calendar, TrendingUp, BarChart3, Clock, DollarSign } from 'lucide-react'
import { processTimeEntries } from '../../utils/timeSplitUtils'

export const TimelineReport = ({ data }) => {
  const [viewMode, setViewMode] = useState('daily') // daily, weekly, monthly

  const timelineData = useMemo(() => {
    if (!data.length) return []

    // Process time entries to handle splits
    const expandedEntries = processTimeEntries(data)
    const grouped = {}
    
    expandedEntries.forEach(entry => {
      let dateKey = entry.entry_date
      
      if (viewMode === 'weekly') {
        const date = new Date(entry.entry_date)
        const startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()))
        dateKey = startOfWeek.toISOString().split('T')[0]
      } else if (viewMode === 'monthly') {
        const date = new Date(entry.entry_date)
        dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0,
          splitEntries: 0,
          users: new Set(),
          projects: new Set(),
          clients: new Set()
        }
      }
      
      const group = grouped[dateKey]
      group.totalHours += entry.duration_hours || 0
      group.entries += 1
      if (entry.is_split_entry) group.splitEntries += 1
      group.users.add(entry.user_id)
      group.projects.add(entry.project_id)
      group.clients.add(entry.client_id)
      
      if (entry.is_billable) {
        group.billableHours += entry.duration_hours || 0
        group.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Object.values(grouped).map(group => ({
      ...group,
      userCount: group.users.size,
      projectCount: group.projects.size,
      clientCount: group.clients.size,
      utilizationRate: group.totalHours > 0 ? (group.billableHours / group.totalHours) * 100 : 0,
      avgRate: group.billableHours > 0 ? group.revenue / group.billableHours : 0,
      originalEntries: group.entries - group.splitEntries
    })).sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [data, viewMode])

  const maxHours = Math.max(...timelineData.map(d => d.totalHours), 1)
  const maxRevenue = Math.max(...timelineData.map(d => d.revenue), 1)
  const totalSplits = timelineData.reduce((sum, d) => sum + d.splitEntries, 0)

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    if (viewMode === 'daily') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    } else if (viewMode === 'weekly') {
      const endOfWeek = new Date(date)
      endOfWeek.setDate(date.getDate() + 6)
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    }
  }

  if (!timelineData.length) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No timeline data found for the selected filters</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-slate-900">Timeline Analysis</h3>
          <div className="flex bg-slate-100 rounded-lg p-1">
            {['daily', 'weekly', 'monthly'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="text-sm text-slate-600">
          {timelineData.length} {viewMode} periods{totalSplits > 0 ? ` • ${totalSplits} splits processed` : ''}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">Total Hours</p>
              <p className="text-2xl font-bold text-blue-900">
                {timelineData.reduce((sum, d) => sum + d.totalHours, 0).toFixed(1)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Total Cost</p>
              <p className="text-2xl font-bold text-green-900">
                ${timelineData.reduce((sum, d) => sum + d.revenue, 0).toFixed(0)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600">Avg {viewMode === 'daily' ? 'Daily' : viewMode === 'weekly' ? 'Weekly' : 'Monthly'} Hours</p>
              <p className="text-2xl font-bold text-purple-900">
                {(timelineData.reduce((sum, d) => sum + d.totalHours, 0) / Math.max(timelineData.length, 1)).toFixed(1)}
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600">Avg Utilization</p>
              <p className="text-2xl font-bold text-orange-900">
                {(timelineData.reduce((sum, d) => sum + d.utilizationRate, 0) / Math.max(timelineData.length, 1)).toFixed(1)}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-slate-900">Activity Timeline</h4>
          <p className="text-sm text-slate-600">Including time split calculations</p>
        </div>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {timelineData.map((period, index) => (
            <div key={period.date} className="border-b border-slate-100 pb-4 last:border-b-0">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-slate-900">{formatDate(period.date)}</h4>
                  <p className="text-sm text-slate-600">
                    {period.originalEntries} entries
                    {period.splitEntries > 0 && ` (+${period.splitEntries} splits)`}
                    • {period.userCount} users • {period.projectCount} projects
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-slate-900">
                    {period.totalHours.toFixed(1)}h
                  </div>
                  <div className="text-sm text-green-600">
                    ${period.revenue.toFixed(0)}
                  </div>
                </div>
              </div>
              
              {/* Hours Bar Chart */}
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>Total Hours</span>
                    <span>{period.totalHours.toFixed(1)}h</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(period.totalHours / maxHours) * 100}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>Billable Hours</span>
                    <span>{period.billableHours.toFixed(1)}h</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(period.billableHours / maxHours) * 100}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>Cost</span>
                    <span>${period.revenue.toFixed(0)}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(period.revenue / maxRevenue) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Utilization Badge */}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-slate-600">Utilization:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    period.utilizationRate >= 80 ? 'bg-green-100 text-green-800' :
                    period.utilizationRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {period.utilizationRate.toFixed(1)}%
                  </span>
                </div>
                <div className="text-xs text-slate-600">
                  Avg Rate: ${period.avgRate.toFixed(0)}/h
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}


export default TimelineReport